import React, { useMemo } from 'react'
import { formatPercent } from '@/features/brazil-elections/utils'
import { PARTY_COLORS, tileOpacity } from './midtermRaces'
import { US_MAP_VIEWBOX, US_STATE_PATHS } from './usGeo'

/* State labels sit on filled land, so they carry a heavy dark halo to stay
   readable on both a faint toss-up fill and a solid runaway one. */
const LABEL_HALO = {
  paintOrder: 'stroke',
  stroke: 'var(--midterm-label-halo, rgba(255,255,255,0.92))',
  strokeWidth: 4.5,
  strokeLinejoin: 'round',
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true

if (typeof document !== 'undefined' && !document.getElementById('midterm-state-in-styles')) {
  const style = document.createElement('style')
  style.id = 'midterm-state-in-styles'
  style.textContent = `@keyframes midterm-state-in { from { opacity: 0; transform: scale(0.94); } to { opacity: 1; transform: none; } }`
  document.head.appendChild(style)
}

const shuffle = items => {
  const next = items.slice()
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[next[i], next[j]] = [next[j], next[i]]
  }
  return next
}

/**
 * The geographic map: every state as its real outline, filled by the party
 * leading its marquee race (fainter = closer), dark seams between states.
 * The lit state is redrawn on top with a white border so it reads above its
 * neighbours. Hover/focus previews a state, click pins it — the same tour
 * contract as the panel beside it.
 *
 * Outlines stagger in on first paint. Party colour is a later fill
 * transition once the markets land, so the map never remounts.
 */
const noop = () => {}

const UsStateMap = ({
  byState,
  activeCode,
  onPreview = noop,
  onPin = noop,
  noMarketLabel,
  revealOnMount = true,
  decorative = false,
}) => {
  const activePath = decorative ? null : US_STATE_PATHS.find(state => state.code === activeCode)
  const reduceMotion = prefersReducedMotion()
  const delays = useMemo(() => {
    if (!revealOnMount || reduceMotion) return null
    const order = shuffle(US_STATE_PATHS.map(state => state.code))
    return Object.fromEntries(order.map((code, index) => [code, index * 0.016]))
  }, [revealOnMount, reduceMotion])

  const revealStyle = code =>
    delays
      ? {
          animation: 'midterm-state-in 180ms ease backwards',
          animationDelay: `${delays[code]}s`,
        }
      : undefined

  return (
    <svg
      viewBox={US_MAP_VIEWBOX}
      className="h-auto w-full"
      role={decorative ? 'presentation' : 'group'}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : 'United States'}>
      {US_STATE_PATHS.map(state => {
        const marquee = byState.get(state.code)?.[0]
        const isActive = !decorative && state.code === activeCode
        const partyFill = marquee?.leader ? PARTY_COLORS[marquee.leader.party] : null
        const lift = {
          transformBox: 'fill-box',
          transformOrigin: 'center',
          transform: isActive ? 'translateY(-5px) scale(1.03)' : undefined,
          filter: isActive
            ? 'drop-shadow(0 14px 16px rgba(0,0,0,0.55))'
            : 'drop-shadow(0 2px 3px rgba(0,0,0,0.35))',
          ...revealStyle(state.code),
        }

        return (
          <g key={state.code}>
            <path
              d={state.d}
              role={decorative ? undefined : 'button'}
              tabIndex={decorative ? undefined : 0}
              aria-pressed={decorative ? undefined : isActive}
              aria-label={
                decorative ? undefined : marquee ? `${state.name} — ${marquee.title}` : `${state.name} — ${noMarketLabel}`
              }
              onMouseEnter={decorative ? undefined : () => onPreview(state.code)}
              onFocus={decorative ? undefined : () => onPreview(state.code)}
              onClick={decorative ? undefined : () => onPin(state.code)}
              onKeyDown={
                decorative
                  ? undefined
                  : event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onPin(state.code)
                      }
                    }
              }
              className={decorative ? 'outline-none' : 'cursor-pointer outline-none'}
              fill="var(--midterm-empty-fill, rgba(15, 40, 52, 0.08))"
              /* A hairline seam only: the float comes from the soft shadow
                 under each state, not from a drawn border. */
              stroke="rgba(6,6,14,0.45)"
              strokeWidth={0.7}
              style={lift}
            />
            {/* Party colour sits on the outline and fades via opacity —
               SVG `fill` itself will not interpolate between a token and a hex. */}
            <path
              d={state.d}
              fill={partyFill || 'transparent'}
              fillOpacity={partyFill ? tileOpacity(marquee) : 0}
              stroke="none"
              pointerEvents="none"
              style={{
                ...lift,
                transition: 'fill-opacity 720ms ease',
              }}
            />
          </g>
        )
      })}

      {/* The lit state's soft halo, floating with it above its neighbours. */}
      {activePath && (
        <path
          d={activePath.d}
          fill="none"
          stroke="var(--midterm-active-stroke, rgba(15, 40, 52, 0.55))"
          strokeWidth={1.6}
          strokeLinejoin="round"
          pointerEvents="none"
          style={{
            transformBox: 'fill-box',
            transformOrigin: 'center',
            transform: 'translateY(-5px) scale(1.03)',
            filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.5))',
            ...revealStyle(activePath.code),
          }}
        />
      )}

      {!decorative &&
        US_STATE_PATHS.map(state => {
          const marquee = byState.get(state.code)?.[0]
          return (
            <text
              key={`label-${state.code}`}
              x={state.labelX}
              y={state.labelY}
              textAnchor="middle"
              pointerEvents="none"
              className="fill-[var(--midterm-label-fill,#16192f)] font-[family-name:var(--font-figures)] font-black [html[data-theme='dark']_&]:fill-white"
              style={{
                fontSize: 13.5,
                ...LABEL_HALO,
                opacity: marquee ? 1 : 0.5,
                transition: 'opacity 720ms ease, transform 300ms ease',
                /* The label rides the lift with its state. */
                transform: state.code === activeCode ? 'translateY(-5px)' : undefined,
                ...revealStyle(state.code),
              }}>
              {state.code}
              <tspan
                x={state.labelX}
                dy={13}
                style={{
                  fontSize: 11.5,
                  ...LABEL_HALO,
                  opacity: marquee?.leader ? 1 : 0,
                  transition: 'opacity 720ms ease',
                }}
                className="font-black fill-[var(--midterm-label-fill,#16192f)] [html[data-theme='dark']_&]:fill-white">
                {marquee?.leader ? formatPercent(marquee.leader.probability) : ''}
              </tspan>
            </text>
          )
        })}
    </svg>
  )
}

export default UsStateMap
